import React, {
  createContext,
  Key,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface Vero1ReviewPanelState {
  dealId: Key | null;
  /** The deal whose page is currently mounted (independent of an open review),
   *  so the rail can show for an existing review before the panel is opened. */
  currentDealId: Key | null;
  dealLabel: string | null;
  isOpen: boolean;
  /** True while a page belonging to the review's deal is mounted — the host
   *  renders nothing anywhere else. */
  inDealContext: boolean;
  openReview: (dealId: Key, dealLabel?: string) => void;
  closeReview: () => void;
  /** Ends the session and drops the deal, so no rail remains to reopen. */
  exitReview: () => void;
  /**
   * Pages that belong to a deal report which one. A review is scoped to the
   * deal it was opened on — landing on a different deal discards it entirely,
   * so a stale checklist can never render against the wrong deal.
   */
  registerDealContext: (currentDealId: Key | null | undefined) => void;
  /** True while the trigger's start is in flight — the panel renders the
   *  skeleton instead of the prior record, whose own Start button would
   *  otherwise offer the same act a second time. */
  startPending: boolean;
  setStartPending: (pending: boolean) => void;
  /** A restart was requested while screening re-runs: the panel holds open,
   *  and starts the draft itself the moment generation reads Ready. */
  autoStartWhenReady: boolean;
  setAutoStartWhenReady: (pending: boolean) => void;
}

const Vero1ReviewPanelContext = createContext<Vero1ReviewPanelState>({
  dealId: null,
  currentDealId: null,
  dealLabel: null,
  isOpen: false,
  inDealContext: false,
  openReview: () => undefined,
  closeReview: () => undefined,
  exitReview: () => undefined,
  registerDealContext: () => undefined,
  startPending: false,
  setStartPending: () => undefined,
  autoStartWhenReady: false,
  setAutoStartWhenReady: () => undefined,
});

/**
 * Holds the panel's open state above the router so a review survives the agent
 * moving between one deal's summary and applicant pages. It does NOT survive
 * moving to another deal: that deal's own trigger (or nothing, if it has no
 * checklist) is the only way in there.
 */
export const Vero1ReviewPanelProvider: React.FC = ({ children }) => {
  const [dealId, setDealId] = useState<Key | null>(null);
  const [dealLabel, setDealLabel] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentDealId, setCurrentDealId] = useState<Key | null>(null);
  const [startPending, setStartPending] = useState(false);
  const [autoStartWhenReady, setAutoStartWhenReady] = useState(false);

  const openReview = useCallback(
    (nextDealId: Key, nextLabel?: string) => {
      setDealId(nextDealId);
      // the rail reopens with only the id — keep the label unless the deal
      // actually changed, or the header degrades to "Deal #id"
      setDealLabel((prev) =>
        nextLabel !== undefined
          ? nextLabel
          : String(nextDealId) === String(dealId)
          ? prev
          : null
      );
      setIsOpen(true);
    },
    [dealId]
  );

  // the deal is deliberately retained on close so a reopen returns to the same
  // review without the caller having to supply the id again
  const closeReview = useCallback(() => setIsOpen(false), []);

  // discard ends the session outright: keeping the deal would leave the rail
  // offering to reopen a panel with no draft behind it
  const exitReview = useCallback(() => {
    setIsOpen(false);
    setDealId(null);
    setDealLabel(null);
    // a pending flag that outlives its start renders every later panel as a
    // permanent skeleton — the trigger's onSettled dies with its unmount
    setStartPending(false);
    setAutoStartWhenReady(false);
  }, []);

  const registerDealContext = useCallback(
    (nextCurrentDealId: Key | null | undefined) => {
      setCurrentDealId(nextCurrentDealId ?? null);
    },
    []
  );

  useEffect(() => {
    if (
      dealId !== null &&
      currentDealId !== null &&
      String(currentDealId) !== String(dealId)
    ) {
      setIsOpen(false);
      setDealId(null);
      setDealLabel(null);
      setStartPending(false);
      setAutoStartWhenReady(false);
    }
  }, [currentDealId, dealId]);

  // the panel belongs to the deal's own pages (deal summary + its
  // application summaries). Anywhere else the host renders nothing; the
  // review itself is retained, so returning to the deal restores it.
  const inDealContext =
    dealId !== null &&
    currentDealId !== null &&
    String(currentDealId) === String(dealId);

  const value = useMemo(
    () => ({
      dealId,
      currentDealId,
      dealLabel,
      isOpen,
      inDealContext,
      openReview,
      closeReview,
      exitReview,
      registerDealContext,
      startPending,
      setStartPending,
      autoStartWhenReady,
      setAutoStartWhenReady,
    }),
    [
      dealId,
      currentDealId,
      dealLabel,
      isOpen,
      inDealContext,
      openReview,
      closeReview,
      exitReview,
      registerDealContext,
      startPending,
      autoStartWhenReady,
    ]
  );

  return (
    <Vero1ReviewPanelContext.Provider value={value}>
      {children}
    </Vero1ReviewPanelContext.Provider>
  );
};

export const useVero1ReviewPanel = () => useContext(Vero1ReviewPanelContext);

/** One-line registration for deal-scoped pages. The cleanup deregisters on
 *  unmount so the panel disappears the moment the agent leaves the deal's
 *  pages — an unregistered page is out of context, not "same as before". */
export const useRegisterVero1DealContext = (
  currentDealId: Key | null | undefined
) => {
  const { registerDealContext } = useVero1ReviewPanel();
  useEffect(() => {
    registerDealContext(currentDealId ?? null);
    return () => registerDealContext(null);
  }, [currentDealId, registerDealContext]);
};
